import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { GetTradesRequest, TradeData } from "@shared/types";
import {
  useGetClosedTradesArgs,
  useGetTradesQueries,
} from "@shared/features/trades/common/hooks";
import {
  filterTrades,
  getSplitActiveAndWaitingCancellationTrades,
  getTradesFiltersSlice,
} from "@shared/features/trades/common/utils";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { preprocessPieChartData } from "@shared/utils/charts";
import { SkipToken } from "@reduxjs/toolkit/query";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";

interface TradePageProviderProps {
  isOpenTrades: boolean;
  inDrawer?: boolean;
  useOnReconnect?: (refetch?: () => void) => void;
  baseGetTradesArgs?: GetTradesRequest | SkipToken;
  setNumTrades?: (numTrades?: number) => void;
}

const TradePageProvider: FC<PropsWithChildren<TradePageProviderProps>> = ({
  isOpenTrades,
  useOnReconnect,
  inDrawer,
  baseGetTradesArgs,
  setNumTrades,
  children,
}) => {
  const backtestingData = useContext(BacktestDetailsContext);
  const {
    openTradesData: backtestOpenTradesData,
    closedTradesData: backtestClosedTradesData,
  } = backtestingData || {};
  const [visitedOpen, setVisitedOpen] = useState(false);
  const [visitedClosed, setVisitedClosed] = useState(false);
  const openTradesFiltersSlice = getTradesFiltersSlice(true, !!inDrawer);
  const closedTradesFiltersSlice = getTradesFiltersSlice(false, !!inDrawer);
  const openTradesFiltersData = useSelector(
    openTradesFiltersSlice.selectors.selectFilters
  );
  const closedTradesFiltersData = useSelector(
    closedTradesFiltersSlice.selectors.selectFilters
  );

  const [filteredQueryOpenTradesData, setFilteredQueryOpenTradesData] =
    useState<TradeData[]>();
  const [filteredQueryClosedTradesData, setFilteredQueryClosedTradesData] =
    useState<TradeData[]>();

  const filteredOpenTradesData =
    backtestOpenTradesData ?? filteredQueryOpenTradesData;
  const filteredClosedTradesData =
    backtestClosedTradesData ?? filteredQueryClosedTradesData;
  const appTheme = useTheme();

  useEffect(() => {
    if (isOpenTrades) setVisitedOpen(true);
    else setVisitedClosed(true);
  }, [isOpenTrades]);

  const getClosedTradesArgs = useGetClosedTradesArgs(
    baseGetTradesArgs,
    closedTradesFiltersSlice
  );

  const {
    getClosedTradesStatus,
    getOpenTradesStatus,
    getClosedTradesFilterStatus,
  } = useGetTradesQueries({
    openTradesArgs: baseGetTradesArgs,
    closedTradesArgs: getClosedTradesArgs,
    getOpen: visitedOpen,
    getClosed: visitedClosed,
    useOnReconnect,
    inDrawer,
  });

  const getTradesQuery = isOpenTrades
    ? getOpenTradesStatus
    : getClosedTradesStatus;
  const queryTradesData = getTradesQuery?.data?.data;
  const queryOpenTradesData = getOpenTradesStatus.data?.data;
  const queryClosedTradesData = getClosedTradesStatus.data?.data;

  const loadingOpenTrades =
    getOpenTradesStatus.isLoading ||
    (getOpenTradesStatus.isUninitialized && !getOpenTradesStatus.isError);
  const loadingClosedTrades =
    getClosedTradesStatus.isLoading ||
    (getClosedTradesStatus.isUninitialized && !getClosedTradesStatus.isError) ||
    getClosedTradesFilterStatus.isLoading;
  const isLoading =
    (isOpenTrades && loadingOpenTrades) ||
    (!isOpenTrades && loadingClosedTrades);

  useEffect(() => {
    if (
      !loadingClosedTrades &&
      !getClosedTradesStatus.isFetching &&
      queryClosedTradesData
    ) {
      const isInitialized = closedTradesFiltersData.loadedFromQuery;
      let parsedData: TradeData[] | undefined = [];
      if (isInitialized)
        parsedData = getClosedTradesStatus?.originalArgs?.is_backtesting
          ? queryClosedTradesData
          : filterTrades(
              queryClosedTradesData,
              closedTradesFiltersData.filters
            );
      setFilteredQueryClosedTradesData(parsedData);
    }
  }, [queryClosedTradesData, closedTradesFiltersData, loadingClosedTrades]);

  useEffect(() => {
    if (
      !loadingOpenTrades &&
      !getOpenTradesStatus.isFetching &&
      queryOpenTradesData
    ) {
      const isInitialized = openTradesFiltersData.loadedFromQuery;
      let parsedData: TradeData[] | undefined = [];
      if (isInitialized)
        parsedData = getOpenTradesStatus?.originalArgs?.is_backtesting
          ? queryOpenTradesData
          : filterTrades(queryOpenTradesData, openTradesFiltersData.filters);
      setFilteredQueryOpenTradesData(parsedData);
    }
  }, [queryOpenTradesData, openTradesFiltersData, loadingOpenTrades]);

  const filteredTradesData = isOpenTrades
    ? filteredOpenTradesData
    : filteredClosedTradesData;

  const [bulkActionTrades] = useMemo(
    () => getSplitActiveAndWaitingCancellationTrades(queryTradesData),
    [queryTradesData]
  );

  const [activeTrades, waitingCancellationTrades] = useMemo(
    () => getSplitActiveAndWaitingCancellationTrades(filteredTradesData),
    [filteredTradesData]
  );

  useEffect(() => {
    setNumTrades?.(filteredTradesData?.length);
  }, [filteredTradesData]);

  const pieChartData = useMemo(() => {
    const pieChartData =
      filteredOpenTradesData?.map((coinData) => {
        return {
          name: coinData.symbol,
          value: coinData.allocated_amount_usd,
        };
      }) || [];

    return preprocessPieChartData(pieChartData, appTheme.colors.pieChartColors);
  }, [appTheme, filteredOpenTradesData]);

  const value = useMemo(() => {
    return {
      isOpenTrades,
      filtersSlice: isOpenTrades
        ? openTradesFiltersSlice
        : closedTradesFiltersSlice,
      getOpenTradesArgs: baseGetTradesArgs as
        | GetTradesRequest
        | undefined
        | SkipToken,
      getTradesQuery,
      filteredTradesData,
      filteredOpenTradesData,
      filteredClosedTradesData,
      activeTrades,
      bulkActionTrades,
      waitingCancellationTrades,
      isLoading,
      isFetching: getTradesQuery.isFetching,
      isUninitialized: getTradesQuery.isUninitialized,
      precisionConfig: getTradesQuery.data?.precision,
      pieChartData,
      getOpenTradesQuery: getOpenTradesStatus,
      getClosedTradesQuery: getClosedTradesStatus,
      isBacktesting: !!backtestingData,
      inDrawer,
    };
  }, [
    isOpenTrades,
    baseGetTradesArgs,
    getTradesQuery,
    filteredTradesData,
    filteredOpenTradesData,
    filteredClosedTradesData,
    activeTrades,
    bulkActionTrades,
    waitingCancellationTrades,
    isLoading,
    pieChartData,
    getOpenTradesStatus,
    getClosedTradesStatus,
    !!backtestingData,
    inDrawer,
  ]);

  return (
    <TradesPageContext.Provider value={value}>
      {children}
    </TradesPageContext.Provider>
  );
};

export default TradePageProvider;
